/**
 * Bootstrap helper: load every persisted font (SHX + TTF) into the
 * in-memory `FontRegistry` at app start.
 *
 * Called once from `App.tsx` inside a `useEffect` on mount (wired in
 * PR-5). Returns a summary so the caller can log or surface a toast
 * like "Loaded 3 fonts, 1 failed" without having to re-inspect the
 * registry.
 *
 * ### Failure modes
 *
 * - `store.loadAll()` itself may reject (e.g. IndexedDB is unavailable
 *   or corrupt). We never propagate that — fonts are a
 *   graceful-degradation feature, so the app continues with an empty
 *   registry and TEXT/MTEXT falls back to the system renderer.
 *
 * - A single font's bytes may be unparseable (e.g. user manually
 *   poisoned IndexedDB, or a legacy version of the parser wrote
 *   something current `parseShxFont` rejects). We skip that entry and
 *   collect it under `failed[]` so the UI can offer a "remove corrupt
 *   font" action later.
 *
 * - A registered TTF may be rejected by the browser's `FontFace` API
 *   (corrupt bytes, unsupported sfnt variant). Same handling as the
 *   SHX case — the entry lands in `failed[]`.
 *
 * The function is resilient to an aborted signal: it returns whatever
 * it managed to register before the abort and surfaces the abort via
 * the `aborted` flag in the result.
 */

import { FontRegistry } from "./fontRegistry";
import type { FontStore } from "./fontStore";

export type HydrationFailure = {
  /** Normalised font key as stored in the backing store. */
  name: string;
  /** Human-readable reason — used for toast / status panel copy. */
  error: string;
};

export type HydrationResult = {
  /** Number of fonts successfully parsed and inserted into the registry. */
  loaded: number;
  /** Fonts that the store returned but the parser rejected. */
  failed: HydrationFailure[];
  /**
   * `true` iff the backing store itself could not be read (IndexedDB
   * unavailable, quota exhausted on read, etc). When set, `loaded` is
   * `0` and `failed` is empty.
   */
  storeUnavailable: boolean;
  /** `true` if the hydration was aborted mid-flight. */
  aborted: boolean;
};

/**
 * Read every font from `store` and register the ones the parser
 * accepts. Never throws — every failure mode is encoded in the result.
 */
export async function hydrateFontsFromStore(
  store: FontStore,
  signal?: AbortSignal
): Promise<HydrationResult> {
  const result: HydrationResult = {
    loaded: 0,
    failed: [],
    storeUnavailable: false,
    aborted: false,
  };

  let entries: Array<{ name: string; bytes: Uint8Array }>;
  try {
    entries = await store.loadAll(signal);
  } catch (error) {
    if (isAbortError(error)) {
      result.aborted = true;
      return result;
    }
    console.warn("[fonts/hydrate] store.loadAll failed:", error);
    result.storeUnavailable = true;
    return result;
  }

  for (const { name, bytes } of entries) {
    if (signal?.aborted) {
      result.aborted = true;
      break;
    }
    try {
      // Pass the stored key as the `hintedName` so TTF uploads that
      // were saved with a user-chosen family (or a DXF-derived
      // typeface name) round-trip to the same CSS family on hydration.
      // SHX ignores the hint and uses its header name instead.
      const parse = await FontRegistry.register(bytes, name);
      if (parse.ok) {
        result.loaded += 1;
      } else {
        result.failed.push({
          name,
          error: `${parse.error.code}: ${parse.error.message}`,
        });
      }
    } catch (error) {
      // Defensive: `FontRegistry.register` never throws in normal
      // operation, but we guard anyway so one corrupt entry cannot
      // take down the whole hydration loop.
      result.failed.push({
        name,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  return result;
}

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException) return error.name === "AbortError";
  if (error && typeof error === "object") {
    const name = (error as { name?: unknown }).name;
    return name === "AbortError";
  }
  return false;
}
